// Máscaras e validações de campos (pt-BR) para o checkout do cardápio.
// Portado do stack homologado (Vendeon web/src/lib/masks.ts), mantendo só o que o
// checkout usa: telefone (WhatsApp), CEP e o helper de dígitos.

export function onlyDigits(v: string): string {
  return (v ?? '').replace(/\D/g, '');
}

/** Telefone BR: (00) 0000-0000 (fixo) ou (00) 00000-0000 (celular). */
export function maskPhoneBR(v: string): string {
  let d = onlyDigits(v);
  // Número colado do WhatsApp costuma vir com o DDI do Brasil grudado
  // (ex.: "+55 91 95642-6246"). Sem remover o DDI antes do slice(0, 11),
  // os 2 primeiros dígitos ("55") viram DDD e os 2 últimos são descartados
  // no truncamento — corrompendo o telefone de forma silenciosa (isPhoneBR
  // ainda aceita, porque "55" também é um DDD válido).
  if ((d.length === 12 || d.length === 13) && d.startsWith('55')) {
    d = d.slice(2);
  } else if ((d.length === 14 || d.length === 15) && d.startsWith('0055')) {
    d = d.slice(4);
  }
  d = d.slice(0, 11);
  // Campo vazio precisa permanecer vazio: com d === '', o replace abaixo
  // casaria um match de largura zero (^(\d{0,2}) aceita zero dígitos) e
  // devolveria "(" em vez de "".
  if (d.length === 0) return '';
  if (d.length <= 2) return d.replace(/^(\d{0,2})/, '($1');
  if (d.length <= 6) return d.replace(/^(\d{2})(\d{0,4})/, '($1) $2');
  if (d.length <= 10) return d.replace(/^(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
  return d.replace(/^(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
}

/** Máscara de CEP: 00000-000. */
export function maskCEP(v: string): string {
  const d = onlyDigits(v).slice(0, 8);
  return d.replace(/^(\d{5})(\d)/, '$1-$2');
}

/** CEP válido: exatamente 8 dígitos. */
export function isCEP(v: string): boolean {
  return onlyDigits(v).length === 8;
}

/** Telefone BR válido: 10 (fixo) ou 11 (celular) dígitos, DDD 11–99. */
export function isPhoneBR(v: string): boolean {
  const d = onlyDigits(v);
  if (d.length !== 10 && d.length !== 11) return false;
  if (Number(d.slice(0, 2)) < 11) return false;
  if (d.length === 11 && d[2] !== '9') return false; // celular começa com 9
  return true;
}

// ── CNPJ (cliente EMPRESA) — aceita o CNPJ ALFANUMÉRICO da Receita (jul/2026): 12 posições [0-9A-Z] + 2
// dígitos verificadores numéricos. Espelha frontend/src/lib/masks.ts (o produto), para o cardápio validar
// igual antes de enviar (evita um 400 no meio do checkout).
export function cnpjChars(v: string): string {
  return (v || '').toUpperCase().replace(/[^0-9A-Z]/g, '');
}

/** Formata CNPJ como `00.000.000/0000-00` (aceita alfanumérico). Idempotente. */
export function maskCNPJ(v: string): string {
  const d = cnpjChars(v).slice(0, 14);
  let out = d.slice(0, 2);
  if (d.length > 2) out += '.' + d.slice(2, 5);
  if (d.length > 5) out += '.' + d.slice(5, 8);
  if (d.length > 8) out += '/' + d.slice(8, 12);
  if (d.length > 12) out += '-' + d.slice(12, 14);
  return out;
}

/** Valida um CNPJ (alfanumérico) pelos dígitos verificadores (módulo 11). Aceita mascarado ou cru. */
export function validarCNPJ(v: string): boolean {
  const d = cnpjChars(v);
  if (d.length !== 14) return false;
  if (!/^[0-9A-Z]{12}[0-9]{2}$/.test(d)) return false;
  let allEqual = true;
  for (let i = 1; i < 14; i++) {
    if (d.charAt(i) !== d.charAt(0)) {
      allEqual = false;
      break;
    }
  }
  if (allEqual) return false;
  // Valor de cada caractere = ASCII − 48 (dígitos = valor numérico; retrocompatível com CNPJ numérico).
  const val = (i: number): number => d.charCodeAt(i) - 48;
  const calc = (len: number): number => {
    let pos = len - 7;
    let sum = 0;
    for (let i = 0; i < len; i++) {
      sum += val(i) * pos;
      pos -= 1;
      if (pos < 2) pos = 9;
    }
    const r = sum % 11;
    return r < 2 ? 0 : 11 - r;
  };
  if (calc(12) !== val(12)) return false;
  return calc(13) === val(13);
}
